import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  BoldIcon,
  BulletListIcon,
  ClearFormatIcon,
  CodeIcon,
  HeadingIcon,
  ItalicIcon,
  LinkIcon,
  NumberedListIcon,
  RedoIcon,
  UnderlineIcon,
  UndoIcon,
  UnlinkIcon,
} from '../Icons'

const Button = ({ title, active, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onMouseDown={event => event.preventDefault()}
        onClick={onClick}
        sx={{
          borderRadius: 1,
          color: active ? 'primary.main' : 'text.secondary',
          bgcolor: active ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
)

export default function Toolbar({ active = {}, disabled, onCommand, onToggleHeading, onLink, onInsertHtml }) {
  const { t } = useTranslation()

  const promptForLink = () => {
    const url = window.prompt(t('Link URL'), active.linkHref ?? '')

    if (url === null) return

    if (!url.trim()) {
      onLink(null)
      return
    }

    onLink(/^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`)
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      sx={{ gap: 0.25, p: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Button title={t('Bold')} active={active.bold} disabled={disabled} onClick={() => onCommand('bold')}>
        <BoldIcon fontSize="small" />
      </Button>
      <Button title={t('Italic')} active={active.italic} disabled={disabled} onClick={() => onCommand('italic')}>
        <ItalicIcon fontSize="small" />
      </Button>
      <Button
        title={t('Underline')}
        active={active.underline}
        disabled={disabled}
        onClick={() => onCommand('underline')}
      >
        <UnderlineIcon fontSize="small" />
      </Button>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

      <Button title={t('Heading')} active={active.heading} disabled={disabled} onClick={onToggleHeading}>
        <HeadingIcon fontSize="small" />
      </Button>
      <Button
        title={t('Bulleted list')}
        active={active.bulletList}
        disabled={disabled}
        onClick={() => onCommand('bulletList')}
      >
        <BulletListIcon fontSize="small" />
      </Button>
      <Button
        title={t('Numbered list')}
        active={active.orderedList}
        disabled={disabled}
        onClick={() => onCommand('orderedList')}
      >
        <NumberedListIcon fontSize="small" />
      </Button>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

      <Button title={t('Add link')} active={active.link} disabled={disabled} onClick={promptForLink}>
        <LinkIcon fontSize="small" />
      </Button>
      <Button title={t('Remove link')} disabled={disabled || !active.link} onClick={() => onLink(null)}>
        <UnlinkIcon fontSize="small" />
      </Button>
      <Button title={t('Clear formatting')} disabled={disabled} onClick={() => onCommand('clear')}>
        <ClearFormatIcon fontSize="small" />
      </Button>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

      <Button title={t('Insert HTML')} disabled={disabled} onClick={onInsertHtml}>
        <CodeIcon fontSize="small" />
      </Button>

      <Box sx={{ flexGrow: 1 }} />

      <Button title={t('Undo')} disabled={disabled} onClick={() => onCommand('undo')}>
        <UndoIcon fontSize="small" />
      </Button>
      <Button title={t('Redo')} disabled={disabled} onClick={() => onCommand('redo')}>
        <RedoIcon fontSize="small" />
      </Button>
    </Stack>
  )
}
